import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #variantController;
  #transition;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#cancelVariantTransition();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const { html: source, productId, newProduct } = event.detail.data;
    if (!source) return;

    // A section can contain more than one product. Only update this product's gallery.
    if (productId && this.dataset.productId !== productId && !newProduct) return;
    const newMediaGallery = Array.from(source.querySelectorAll('media-gallery')).find(
      (gallery) =>
        gallery.dataset.blockId === this.dataset.blockId &&
        gallery.dataset.productId === (productId || this.dataset.productId)
    );
    if (!newMediaGallery) return;

    this.#cancelVariantTransition();
    const controller = new AbortController();
    this.#variantController = controller;
    const gsap = window.gsap;
    const reduceMotion =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      document.documentElement.classList.contains('zilius-motion-reduced');
    const animate =
      gsap && !reduceMotion && this.dataset.hasMedia === 'true' && newMediaGallery.dataset.hasMedia === 'true';

    if (!animate) {
      this.#replaceGallery(newMediaGallery);
      return;
    }

    // Keep the current photo visible while the new cover loads. A newer selection cancels this swap.
    await this.#prepareCover(newMediaGallery, controller.signal);
    if (controller.signal.aborted || !this.isConnected) return;

    this.#transition = gsap.to(this, {
      opacity: 0,
      duration: 0.06,
      ease: 'power1.out',
      overwrite: 'auto',
      onComplete: () => {
        this.#replaceGallery(newMediaGallery);
        if (newMediaGallery instanceof MediaGallery) newMediaGallery.#animateIn(gsap);
      },
    });
  };

  /** Cancel pending media work as soon as the customer chooses another variant. */
  #handleVariantSelected = (event) => {
    const picker = event.target;
    if (picker instanceof HTMLElement && picker.dataset.productId !== this.dataset.productId) return;
    this.#cancelVariantTransition();
  };

  #cancelVariantTransition() {
    this.#variantController?.abort();
    this.#variantController = undefined;
    this.#transition?.kill();
    this.#transition = undefined;
    this.style.removeProperty('opacity');
  }

  /** @param {HTMLElement} newMediaGallery */
  #replaceGallery(newMediaGallery) {
    // Keep the container available when only some variants have uploaded images.
    const hasMedia = newMediaGallery.dataset.hasMedia === 'true';
    const mediaContainer = this.closest('.product-information__media');
    if (mediaContainer) mediaContainer.toggleAttribute('hidden', !hasMedia);
    this.closest('[data-product-grid-content]')?.classList.toggle('product-information--media-none', !hasMedia);

    this.replaceWith(newMediaGallery);
  }

  /** @param {typeof window.gsap} gsap */
  #animateIn(gsap) {
    this.#transition = gsap.fromTo(this, { opacity: 0 }, {
      opacity: 1,
      duration: 0.14,
      ease: 'power2.out',
      clearProps: 'opacity',
      overwrite: 'auto',
      onComplete: () => { this.#transition = undefined; },
    });
  }

  /**
   * Warm the responsive cover without holding up a variant on slow or failed image requests.
   * @param {HTMLElement} gallery
   * @param {AbortSignal} signal
   * @returns {Promise<void>}
   */
  #prepareCover(gallery, signal) {
    const cover = gallery.querySelector('slideshow-slide img, .media-gallery__grid img');
    if (!(cover instanceof HTMLImageElement) || signal.aborted) return Promise.resolve();

    return new Promise((resolve) => {
      const image = new Image();
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        window.clearTimeout(timeout);
        signal.removeEventListener('abort', finish);
        image.onload = null;
        image.onerror = null;
        resolve();
      };
      const timeout = window.setTimeout(finish, 1800);
      const decode = () => image.decode().then(finish, finish);
      signal.addEventListener('abort', finish, { once: true });
      image.onload = decode;
      image.onerror = finish;
      image.sizes = cover.sizes;
      image.srcset = cover.srcset;
      image.src = cover.src;
      if (image.complete) decode();
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
